import React, { useEffect, useState } from "react"
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Link } from "react-router-dom"
import seedEsperienze from "../../data/seedEsperienze"
import seedSubEsperienze from "../../data/seedSubEsperienze"

const EsperienzeSearch = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)

  const findParentCategory = (subEspCategory) => {
    const categoryMap = {
      "Acque e suolo": "acque-e-suolo",
      Territorio: "territorio",
      Energia: "energia",
      Ecoturismo: "ecoturismo",
      Formazione: "formazione",
    }

    return (
      categoryMap[subEspCategory] ||
      subEspCategory.toLowerCase().replace(/\s+/g, "-")
    )
  }

  const searchEsperienze = (term) => {
    if (!term.trim()) return []

    const termLower = term.toLowerCase()
    const results = []

    seedEsperienze.forEach((esp) => {
      if (
        esp.title.toLowerCase().includes(termLower) ||
        esp.subtitle.toLowerCase().includes(termLower)
      ) {
        results.push({
          type: "esperienza",
          id: esp.id,
          title: esp.title,
          subtitle: esp.subtitle,
          category: "Categoria principale",
          link: `/esperienze/${esp.id}`,
        })
      }
    })

    seedSubEsperienze.forEach((subEsp) => {
      const titleMatch = subEsp.title.toLowerCase().includes(termLower)
      const categoryMatch = subEsp.category.toLowerCase().includes(termLower)

      const descriptionMatch = subEsp.imgs.some(
        (img) =>
          img.description && img.description.toLowerCase().includes(termLower)
      )

      const locationMatch = subEsp.imgs.some(
        (img) => img.location && img.location.toLowerCase().includes(termLower)
      )

      if (titleMatch || categoryMatch || descriptionMatch || locationMatch) {
        const parentCategoryId = findParentCategory(subEsp.category)

        results.push({
          type: "sub-esperienza",
          id: subEsp.id,
          title: subEsp.title,
          category: subEsp.category,
          parentCategory: subEsp.category,
          link: `/esperienze/${parentCategoryId}/${subEsp.id}`,
          matchType: titleMatch
            ? "title"
            : categoryMatch
            ? "category"
            : descriptionMatch
            ? "description"
            : "location",
        })
      }
    })

    return results.slice(0, 10) // Limita a 10 risultati
  }

  useEffect(() => {
    if (searchTerm.trim()) {
      setIsSearching(true)
      const timer = setTimeout(() => {
        const results = searchEsperienze(searchTerm)
        setSearchResults(results)
        setIsSearching(false)
      }, 300) // Debounce di 300ms

      return () => clearTimeout(timer)
    } else {
      setSearchResults([])
      setIsSearching(false)
    }
  }, [searchTerm])

  const clearSearch = () => {
    setSearchTerm("")
    setSearchResults([])
  }

  const highlightText = (text, searchTerm) => {
    if (!searchTerm.trim()) return text

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    )
    const parts = text.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-warning">
          {part}
        </mark>
      ) : (
        part
      )
    )
  }

  return (
    <div className="esperienza-search mb-4">
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faSearch} className="me-2" />
            Cerca Esperienze
          </h5>
        </div>
        <div className="card-body">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Cerca per titolo, categoria, descrizione o location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={clearSearch}
                title="Cancella ricerca"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          {searchTerm && (
            <div className="mt-3">
              {isSearching ? (
                <div className="text-center py-3">
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Ricerca...</span>
                  </div>
                  <span className="ms-2">Ricerca in corso...</span>
                </div>
              ) : (
                <>
                  {searchResults.length > 0 ? (
                    <>
                      <h6 className="text-muted mb-3">
                        Trovati {searchResults.length} risultati per "
                        {searchTerm}"
                      </h6>
                      <div className="list-group">
                        {searchResults.map((result, index) => (
                          <Link
                            key={`${result.type}-${result.id}-${index}`}
                            to={result.link}
                            className="list-group-item list-group-item-action"
                            onClick={clearSearch}
                          >
                            <div className="d-flex w-100 justify-content-between">
                              <h6 className="mb-1">
                                {highlightText(result.title, searchTerm)}
                              </h6>
                              <small
                                className={`badge ${
                                  result.type === "esperienza"
                                    ? "bg-primary"
                                    : "bg-secondary"
                                }`}
                              >
                                {result.type === "esperienza"
                                  ? "Categoria"
                                  : "Progetto"}
                              </small>
                            </div>
                            <p className="mb-1 text-muted">
                              <strong>Categoria:</strong>{" "}
                              {highlightText(result.category, searchTerm)}
                              {result.parentCategory &&
                                result.parentCategory !== result.category && (
                                  <span> → {result.parentCategory}</span>
                                )}
                            </p>
                            {result.subtitle && (
                              <small className="text-muted">
                                {highlightText(result.subtitle, searchTerm)}
                              </small>
                            )}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="alert alert-info mb-0">
                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                      Nessun risultato trovato per "{searchTerm}"
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EsperienzeSearch
